import React, { useState } from 'react';
import i18next from '../../../core/i18n';
import styled from 'styled-components';
import { FinancingUser } from '@indigo-ag/schema';
import { IconButton, palettes, SelectChangeEvent, TextCallout } from '@indigo-ag/component-module';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';

import { createStylesHook, text, UserUtil, utils } from 'shared';
import { CreditAnalystSelector, CreditAnalystSelectorProps } from '../CreditAnalystSelector/CreditAnalystSelector';
import { CREDIT_ANALYST_DEFAULT_VALUES } from '../../../features/brazil-grower/shared-br-grower';

interface EditableCreditAnalystProps {
  readonly?: boolean;
  creditAnalystAssigned?: FinancingUser;
}

const useStyles = createStylesHook({
  creditAnalystText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '115px'
  },
  editIconButton: {
    '& > svg': {
      color: palettes.coastal.core.mediumBlue
    }
  }
});

const UnassignedTextWrapper = styled.div`
  > div {
    padding: 0;
  }
`;

const CreditAnalystLabel: React.FunctionComponent<{ creditAnalystAssigned?: FinancingUser }> = ({
  creditAnalystAssigned
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  return creditAnalystAssigned ? (
    <span className={classes.creditAnalystText}>{UserUtil.getUserFullName(creditAnalystAssigned)}</span>
  ) : (
    <UnassignedTextWrapper>
      <TextCallout label={text.toTitleCase(i18next.t('unassigned'))} variant="warning" />
    </UnassignedTextWrapper>
  );
};

export const EditableCreditAnalyst: React.FunctionComponent<EditableCreditAnalystProps &
  CreditAnalystSelectorProps> = ({ readonly = false, creditAnalystAssigned, onChange, ...selectProps }) => {
  /**
   * Styles
   */
  const classes = useStyles();
  /**
   * State
   */
  const [isEditing, setIsEditing] = useState(false);
  const [preSelectedAnalystId, setPreSelectedAnalystId] = useState<string>('');
  /**
   * Handlers
   */
  const onSelectAnalyst = (event: SelectChangeEvent) => {
    setIsEditing(false);
    if (onChange) onChange(event);
  };

  const onEditAnalyst = () => {
    setIsEditing(true);
    setPreSelectedAnalystId(creditAnalystAssigned?.id || CREDIT_ANALYST_DEFAULT_VALUES.UNASSIGNED);
  };

  return (
    <>
      {isEditing ? (
        <CreditAnalystSelector
          initialOptions={[
            {
              name: utils.text.firstLetterUppercase(i18next.t('unassigned')),
              value: CREDIT_ANALYST_DEFAULT_VALUES.UNASSIGNED
            }
          ]}
          onChange={onSelectAnalyst}
          preSelectedAnalystId={preSelectedAnalystId}
          {...selectProps}
        />
      ) : (
        <>
          <CreditAnalystLabel creditAnalystAssigned={creditAnalystAssigned} />
          {!readonly && (
            <IconButton
              className={classes.editIconButton}
              colorVariant="primary"
              icon={faEdit}
              label="edit credit analyst"
              onClick={onEditAnalyst}
            />
          )}
        </>
      )}
    </>
  );
};
